import React, { useState } from 'react';
import { Flame, ShoppingBag, Archive, Info, DollarSign, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';

interface DropActionsModalProps {
  drop: {
    id: string;
    rarity: string;
    serial_number: string;
    media_url: string;
    unit_value_cents?: number;
  };
  onClose: () => void;
}

const DropActionsModal: React.FC<DropActionsModalProps> = ({ drop, onClose }) => {
  const { toast } = useToast();
  const [action, setAction] = useState<'choose' | 'burn' | 'list' | 'stash'>('choose');
  const [processing, setProcessing] = useState(false);

  const unitValue = drop.unit_value_cents || 0;
  const burnValue = unitValue; // 100% to user (creator already paid 2x)

  const handleBurn = async () => {
    setProcessing(true);
    try {
      // Call edge function to process burn
      const { data, error } = await supabase.functions.invoke('burn-drop', {
        body: { drop_id: drop.id },
      });

      if (error) throw error;

      toast({
        title: '🔥 Drop Burned!',
        description: `You earned $${(data.value_earned_cents / 100).toFixed(2)}. ${
          data.threshold_met 
            ? 'You can now transfer to Stripe!' 
            : `$${((data.threshold_cents - data.new_balance_cents) / 100).toFixed(2)} more to transfer.`
        }`,
      });

      // Close modal and refresh
      setTimeout(() => {
        onClose();
        window.location.reload(); // Refresh to update balance
      }, 2000);
    } catch (error: any) {
      toast({
        title: 'Burn failed',
        description: error.message,
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  const handleList = async () => {
    setProcessing(true);
    try {
      // Update drop status to listed
      const { error } = await supabase
        .from('drops')
        .update({
          user_action: 'listed',
        })
        .eq('id', drop.id);

      if (error) throw error;

      toast({
        title: '📦 Listed on Marketplace',
        description: 'Your drop is now available for sale. Set your price in the marketplace.',
      });

      // Navigate to marketplace listing page
      setTimeout(() => {
        onClose();
        window.location.href = '/marketplace/list/' + drop.id;
      }, 1500);
    } catch (error: any) {
      toast({
        title: 'Failed to list',
        description: error.message,
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  const handleStash = async () => {
    setProcessing(true);
    try {
      // Update drop status to stashed
      const { error } = await supabase
        .from('drops')
        .update({
          user_action: 'stashed',
          stashed_at: new Date().toISOString(),
        })
        .eq('id', drop.id);

      if (error) throw error;

      toast({
        title: '📦 Stashed',
        description: 'Drop saved to your inventory. Access it anytime from your profile.',
      });

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      toast({
        title: 'Failed to stash',
        description: error.message,
        variant: 'destructive',
      });
      setProcessing(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => !processing && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">What would you like to do?</DialogTitle>
          <DialogDescription>
            Choose how to use your {drop.rarity} drop #{drop.serial_number}
          </DialogDescription>
        </DialogHeader>

        {/* Drop Preview */}
        <div className="flex justify-center mb-6">
          <div className="w-48 aspect-[2/3] rounded-xl overflow-hidden shadow-lg">
            <img
              src={drop.media_url}
              alt={drop.rarity}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {action === 'choose' && (
          <div className="space-y-3">
            {/* Burn Option */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setAction('burn')}
              className="w-full p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 border-2 border-orange-500/30 hover:border-orange-500/50 rounded-xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/30 transition-colors">
                  <Flame className="w-6 h-6 text-orange-500" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1 flex items-center gap-2">
                    Burn for Cash
                    <DollarSign className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    Convert to earnings instantly
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    +${(burnValue / 100).toFixed(2)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Added to your FOMO balance
                  </div>
                </div>
              </div>
            </motion.button>

            {/* List Option */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setAction('list')}
              className="w-full p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-2 border-blue-500/30 hover:border-blue-500/50 rounded-xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <ShoppingBag className="w-6 h-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1 flex items-center gap-2">
                    List on Marketplace
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Sell to other users for more value
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    You keep 85% • FOMO takes 15% fee
                  </div>
                </div>
              </div>
            </motion.button>

            {/* Stash Option */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setAction('stash')}
              className="w-full p-4 bg-gradient-to-r from-gray-500/10 to-slate-500/10 border-2 border-gray-500/30 hover:border-gray-500/50 rounded-xl text-left transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-500/20 flex items-center justify-center group-hover:bg-gray-500/30 transition-colors">
                  <Archive className="w-6 h-6 text-gray-500" />
                </div>
                <div className="flex-1">
                  <div className="font-bold text-lg mb-1">Save to Inventory</div>
                  <div className="text-sm text-muted-foreground">
                    Keep as collectible • Decide later
                  </div>
                </div>
              </div>
            </motion.button>

            {/* Info Notice */}
            <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
              <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                <strong>Burn</strong> is instant and permanent. <strong>List</strong> lets you sell for potentially more. 
                <strong> Stash</strong> keeps it safe in your inventory.
              </p>
            </div>
          </div>
        )}

        {/* Burn Confirmation */}
        {action === 'burn' && (
          <div className="space-y-4">
            <div className="p-6 bg-orange-500/10 border-2 border-orange-500/30 rounded-xl text-center">
              <Flame className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Burn this drop?</h3>
              <p className="text-muted-foreground mb-4">
                This action is permanent and cannot be undone.
              </p>
              <div className="text-4xl font-bold text-green-600 mb-2">
                +${(burnValue / 100).toFixed(2)}
              </div>
              <p className="text-sm text-muted-foreground">
                Will be added to your FOMO balance
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setAction('choose')}
                disabled={processing}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleBurn}
                disabled={processing}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Burning...
                  </>
                ) : (
                  <>
                    <Flame className="w-4 h-4 mr-2" />
                    Confirm Burn
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* List Confirmation */}
        {action === 'list' && (
          <div className="space-y-4">
            <div className="p-6 bg-blue-500/10 border-2 border-blue-500/30 rounded-xl text-center">
              <ShoppingBag className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">List on Marketplace?</h3>
              <p className="text-muted-foreground mb-4">
                Set your price and sell to other users
              </p>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>• You set the selling price</div>
                <div>• You keep 85% of sale</div>
                <div>• FOMO takes 15% fee</div>
                <div>• Can unlist anytime before sale</div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setAction('choose')}
                disabled={processing}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleList}
                disabled={processing}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Listing...
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Continue to List
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Stash Confirmation */}
        {action === 'stash' && (
          <div className="space-y-4">
            <div className="p-6 bg-gray-500/10 border-2 border-gray-500/30 rounded-xl text-center">
              <Archive className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Save to Inventory?</h3>
              <p className="text-muted-foreground mb-4">
                Keep this drop safe in your collection
              </p>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>• Access anytime from your profile</div>
                <div>• Burn or list later</div>
                <div>• Show off your collection</div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setAction('choose')}
                disabled={processing}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleStash}
                disabled={processing}
                className="flex-1"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Stashing...
                  </>
                ) : (
                  <>
                    <Archive className="w-4 h-4 mr-2" />
                    Stash Drop
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DropActionsModal;
